"use client";

// Floating Messenger button — มุมขวาล่าง คลิกเปิดแชท FB Messenger ใน tab ใหม่
// ใช้ใน LandingPage (ทักทีม FlowCloser) หรือ Sale Page (ทักร้านของลูกค้า)

import { useState, useEffect } from "react";
import { X } from "lucide-react";

interface Props {
  url: string;
  /** ข้อความ tooltip โผล่ขึ้นมาตอน mount เพื่อ catch attention */
  greeting?: string;
  /** delay ms ก่อน greeting เด้งขึ้น */
  greetingDelayMs?: number;
}

const STORAGE_KEY = "flowcloser:messenger-fab-greeting-dismissed";

export default function MessengerFAB({
  url,
  greeting = "สวัสดีค่ะ มีอะไรให้ช่วยไหมคะ?",
  greetingDelayMs = 2500,
}: Props) {
  const [showGreeting, setShowGreeting] = useState(false);

  useEffect(() => {
    // ถ้า user เคย dismiss แล้วในเซสชันนี้ ไม่ต้องเด้งซ้ำ
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

    const timer = setTimeout(() => setShowGreeting(true), greetingDelayMs);
    return () => clearTimeout(timer);
  }, [greetingDelayMs]);

  const dismissGreeting = () => {
    setShowGreeting(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed z-50"
      style={{
        right: "max(16px, env(safe-area-inset-right))",
        bottom: "max(16px, env(safe-area-inset-bottom))",
      }}
    >
      {/* Greeting bubble */}
      {showGreeting && (
        <div
          className="absolute right-0 mb-2 flex items-center gap-2 animate-in slide-in-from-bottom-2 fade-in duration-300"
          style={{
            bottom: "100%",
            background: "#fff",
            border: "1px solid rgba(11,20,55,.08)",
            borderRadius: 14,
            padding: "10px 14px",
            paddingRight: 36,
            fontSize: 13,
            fontWeight: 500,
            color: "#0b1437",
            boxShadow: "0 12px 30px -12px rgba(11,20,55,.18)",
            whiteSpace: "nowrap",
            maxWidth: "calc(100vw - 32px)",
          }}
        >
          {greeting}
          <button
            onClick={dismissGreeting}
            aria-label="ปิด"
            className="absolute top-1/2 right-2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
            style={{ color: "#94a3b8" }}
          >
            <X size={12} />
          </button>
          {/* tail */}
          <span
            style={{
              position: "absolute",
              right: 18,
              bottom: -6,
              width: 12,
              height: 12,
              background: "#fff",
              borderRight: "1px solid rgba(11,20,55,.08)",
              borderBottom: "1px solid rgba(11,20,55,.08)",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      )}

      {/* The button */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="แชท Messenger"
        title="แชทกับเรา"
        className="group flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, #00B2FF 0%, #006AFF 50%, #8A3FFC 100%)",
          boxShadow:
            "0 8px 24px -6px rgba(0,106,255,.45), 0 0 0 4px rgba(255,255,255,.08)",
        }}
      >
        {/* Messenger lightning bolt icon */}
        <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
          <path
            d="M18 4C10.27 4 4 9.78 4 16.91C4 21.04 6 24.7 9.13 27.06V32L13.69 29.5C14.96 29.86 16.45 30.06 18 30.06C25.73 30.06 32 24.28 32 17.15C32 10.02 25.73 4 18 4Z"
            fill="#fff"
          />
          <path
            d="M9.31 21.39L13.5 14.81L17.66 18.2L22.5 14.81L17.79 21.39L13.69 18.05L9.31 21.39Z"
            fill="url(#mfg)"
          />
          <defs>
            <linearGradient id="mfg" x1="9" y1="14" x2="22" y2="22" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#00B2FF" />
              <stop offset=".5" stopColor="#006AFF" />
              <stop offset="1" stopColor="#8A3FFC" />
            </linearGradient>
          </defs>
        </svg>
      </a>
    </div>
  );
}
